import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { BaseUser, UpdateType } from 'messages/websocket.messages';

import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';

@Component({
  selector: 'app-qa-raise-hand',
  templateUrl: './qa-raise-hand.component.html',
  styleUrls: ['./qa-raise-hand.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QARaiseHandComponent extends Loggable implements OnInit, OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  protected qaEnabled = false;
  protected isHandRaised = false;
  protected isUserPresenting = false;
  private user?: User;
  private isCrossRoom = false;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('QARaiseHandComponent');
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => {
        this.isCrossRoom = isCrossRoom;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.isQueuedQAndAActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isQAActive) => {
        this.qaEnabled = isQAActive;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isQueuedToSpeak)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHandRaised) => {
        this.isHandRaised = isHandRaised;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting = isUserPresenting;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async raiseHand() {
    const participant = this.getParticipantInfo();
    await SocialStateMachine.shared
      .requestAddParticipantToSpeakingQueue({
        participant,
        userId: participant.userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request add participant to speaking queue', e));
  }

  public async lowerHand() {
    const participant = this.getParticipantInfo();
    await SocialStateMachine.shared
      .requestRemoveParticipantFromSpeakingQueue({
        participant,
        userId: participant.userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request remove participant from speaking queue', e));
  }

  private getParticipantInfo(): BaseUser {
    const { firstName, lastName, userId } = this.user ?? {};
    return { firstName: firstName ?? '', lastName: lastName ?? '', userId: userId ?? '' };
  }
}
